import {
    AppId,
    FCX,
    ID,
    ProductLogo, sanitize,
    TextInput,
    usePlatformContext,
} from "@datassential/platform-ui-lib";
import { useContext, useEffect, useMemo, useState } from "react";
import { iUseMenuExamplesParams, useMenuExamples } from "@apiHooks/useMenuExamples";
import { useSelector } from "react-redux";
import { currentCountrySelector } from "@selectors/countries.selector";
import { useMenuExampleTableConfig } from "@vms/MenuExampleTable";
import { SegmentsEnum } from "@models/Byo";
import Tabs from "@components/Tabs/Tabs";
import { IoIosSearch } from "react-icons/io";
import VisibilitySensor from "react-visibility-sensor";
import DownloadBtn from "@components/DownloadBtn/DownloadBtn";
import { Table } from "@components/TableNew";
import { TableColumnsContext } from "@components/TableNew/TableColumns";
import { GlobalContext } from "@components/App/App.context";

const SEGMENTS = [
    { id: SegmentsEnum.QSR, name: 'Quick Service' },
    { id: SegmentsEnum.FastCasual, name: 'Fast Casual' },
    { id: SegmentsEnum.Midscale, name: 'Midscale Dining' },
    { id: SegmentsEnum.CasualDining, name: 'Casual Dining' },
    { id: SegmentsEnum.FineDining, name: 'Fine Dining' },
];

const MenuExamplesResultsTable: FCX<{
    query: string;
    selectedStatesIds: ID[];
    isChainsEnabled: boolean;
}> = ({
    query,
    selectedStatesIds,
    isChainsEnabled,
}) => {
    const { userInfo: { products} } = usePlatformContext();
    const { getRem } = useContext(GlobalContext);

    const isLocalProductEnabled = useMemo(
        () => {
            return !products[AppId.LocalInsights]?.isDisabled;
        },
        [products]
    );

    const localProductLink = useMemo(
        () => {
            return products[AppId.LocalInsights]?.url;
        },
        [products]
    );

    const countryCode = useSelector(currentCountrySelector);

    const [selectedSegmentId, setSelectedSegmentId] = useState<ID>(SEGMENTS[0].id);
    const [additionalQuery, setAdditionalQuery] = useState<string>('');
    const [submittedAdditionalQuery, setSubmittedAdditionalQuery] = useState<string>('');

    const useMenuExamplesParams = useMemo(
        (): iUseMenuExamplesParams => ({
            search: query,
            statesIds: selectedStatesIds,
            selectedSegmentId,
            includeChains: isChainsEnabled,
            tableSearch: submittedAdditionalQuery,
            countryId: countryCode,
        }),
        [query, selectedStatesIds, isChainsEnabled, countryCode, submittedAdditionalQuery, selectedSegmentId]
    );

    const messageText = useMemo(
        () => {
            let result = `Dishes with <b>“${query.trim()}”</b>`;

            if (submittedAdditionalQuery.trim() !== '') {
                result += ` and <i>“${submittedAdditionalQuery.trim()}”</i>`;
            }

            return result;
        },
        [query, submittedAdditionalQuery]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setSubmittedAdditionalQuery(additionalQuery);
        }, 300);
        return () => clearTimeout(timer);
    }, [additionalQuery]);

    const {
        isLoading,
        data,
        inc,
        isLoaded,
        download,
        isMainTermLocalKeyword,
        mainTermKeywordId,
    } = useMenuExamples(useMenuExamplesParams);

    const columns = useMenuExampleTableConfig(data);

    const itemsCount = useMemo(() => {
        if (!isLoading) return data.length;
        return Math.max(30, data.length);
    }, [isLoading, data.length]);

    const columnsContextValue = useMemo(() => ({ data: columns }), [columns]);

    return (
        <>
            <div
                className="MenuExamples__message"
            >
                <div
                    dangerouslySetInnerHTML={{ __html: messageText }}
                    data-testid={'textMenuExamplesSearchMessage'}
                />
                {isLocalProductEnabled && !!mainTermKeywordId && isMainTermLocalKeyword && (
                    <div className="MenuExamples__sub-message">
                        for a heat map of ”{query}” by market, go to
                        <a
                            href={sanitize(`${localProductLink}/${process.env.REACT_APP_LOCAL_KEYWORD_LINK}/${mainTermKeywordId}`)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <ProductLogo
                                appId={AppId.LocalInsights}
                                type="regular"
                                style={{
                                    width: 16,
                                    height: 16,
                                    marginLeft: 6,
                                }}
                            />
                        </a>
                    </div>
                )}
                <div
                    className="MenuExamples__download"
                    data-testid="buttonMenuExamplesDownload"
                >
                    <DownloadBtn onClick={download}/>
                </div>
            </div>
            <div className="MenuExamples__results">
                <div className="MenuExamples__results-header">
                    <Tabs
                        data={SEGMENTS}
                        activeItemId={selectedSegmentId}
                        handleClick={setSelectedSegmentId}
                        testId="selectMenuExamplesSegments"
                    />
                    <TextInput
                        className="MenuExamples__results-search"
                        value={additionalQuery}
                        setValue={setAdditionalQuery}
                        placeholder="Search within results..."
                        testId="inputMenuExamplesTableSearch"
                    >
                        <IoIosSearch className="MenuExamples__results-search-icon"/>
                    </TextInput>
                </div>
                <div
                    className="MenuExamples__table"
                    data-testid="tableMenuExamplesResults"
                >
                    <TableColumnsContext.Provider value={columnsContextValue}>
                        <Table
                            rowsCount={itemsCount}
                            isLoading={isLoading}
                            rowHeight={getRem(60)}
                            showFallback
                            testId="menuExamplesTable"
                        />
                    </TableColumnsContext.Provider>
                    {!isLoaded && !isLoading && (
                        <VisibilitySensor
                            onChange={(visible) => {
                                if (visible) {
                                    inc();
                                }
                            }}
                            partialVisibility={true}
                        >
                            <div className="MenuExamples__table-loader"/>
                        </VisibilitySensor>
                    )}
                </div>
            </div>
        </>
    );
};

export default MenuExamplesResultsTable;